/* eslint-disable consistent-return */
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Tab } from 'semantic-ui-react';
import {
  Condition,
  FinanceApplicationStatus,
  FinanceResponseStatus,
  FinanceType,
} from '@upstart/harmony/lib/constants';
import { EventType } from '@upstart/harmony/lib/types/websocketTypes';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import ChangeLog from '@upstart/harmony/lib/pencil-changelog';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import {
  FinanceApplication,
  FinanceResponse,
} from '@upstart/harmony/lib/legos/financingTypes';
import uniq from 'lodash/uniq';
import isNumber from 'lodash/isNumber';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';

import { hasLeaseOffers } from 'util/pencilUtils';
import { getSelectedFinanceType } from 'util/paymentUtils';
import {
  shouldDisplayFinanceType,
  shouldShowFinanceTab,
} from 'util/deskingUtils';
import {
  sendData,
  getCarInfo,
  safeParseStringEventObject,
} from 'util/amplitude';
import {
  amplitudeEventsDesking,
  amplitudeEventsDeskingCTR,
} from 'util/amplitudeEvents';
import RehashOffers from 'containers/RehashOffers';
import DeskingOfferList, { DeskingOfferListProps } from './DeskingOfferList';
import LendingOffers from '../../../containers/PencilEditor/LendingOffers';
import { DeskingTab, Tabs } from '../../../containers/PencilEditor/types';
import FinanceApplicationService from '../../../services/FinanceApplicationService';
import FinanceResponseService from '../../../services/FinanceResponseService';
import './DeskingOffers.scss';
import LoadingOverlay from '../../LoadingOverlay';
import { isEmbeddedInFniHome } from '../../../util/commandCenter';
import { useGrowthBook } from '../../../hooks/GrowthBook';

const deskingTabTitles: Record<DeskingTab, string> = {
  [FinanceType.PURCHASE]: 'Purchase',
  [FinanceType.LEASE]: 'Lease',
  [FinanceType.CASH]: 'Cash',
  [Tabs.SELECTED]: 'Selected Pencils',
  [Tabs.FINANCE_OFFERS]: 'Finance Offers',
} as const;

const deskingTabs = Object.keys(deskingTabTitles) as DeskingTab[];

type LendingProps = {
  updatePencil: (pencil: Pencil) => void;
  updateFinanceApplicationError: (msg: string | null) => void;
  onSubmit: () => void;
  setFinanceOfferCount: (count: number) => void;
  setFinanceApplicationCount: (count: number) => void;
  selectedFinanceApplication: FinanceApplication;
  showRicTab: boolean;
  setShowRicTab: (isShown: boolean) => void;
  showCustomerSelectedOffer: boolean;
  setShowCustomerSelectedOffer: (isShown: boolean) => void;
  financeApplicationCount: number;
  financeOfferCount: number;
};

interface Props extends DeskingOfferListProps {
  deskingOffers: DeskingOffer[];
  updateDeskingOffers: (
    offers: DeskingOffer[],
    params?: { computePayments: boolean; saveToGlobalState: boolean },
  ) => Promise<void>;
  onResetToDefault?: any;
  changeLog?: ChangeLog;
  lendingOffersProps: LendingProps;
  isFinanceOffersTab?: boolean;
  setTabPath: (val: boolean) => void;
  forwardDealToFNI: (payload: {
    dealId: string;
    submitToLenders: boolean;
    sendToUpstart: boolean;
    skipActiveOfferInvalidation: boolean;
    selectedLenderIds?: string[];
  }) => void;
  financeResponses: FinanceResponse[] | null;
  setFinanceResponses: (responses: FinanceResponse[] | null) => void;
}

const DeskingOffers: FunctionComponent<Props> = (props: Props) => {
  const {
    car,
    deal,
    dealer,
    desking,
    deskingOffers,
    deskingPayments,
    errors,
    lendingOffersProps,
    pencil,
    removeExpiredIncentivesRequest,
    removeUnavailableAccessoriesRequest,
    isFinanceOffersTab,
    setTabPath,
    financeResponses,
    setFinanceResponses,
  } = props;

  const [loading, setLoading] = useState(false);

  const [financeApplications, setFinanceApplications] = useState<
    FinanceApplication[] | null
  >(null);

  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const gb = useGrowthBook();

  const visibleTabs = useMemo(
    () =>
      deskingTabs.filter(deskingTab => {
        if (isEmbeddedInFniHome()) {
          return deskingTab === Tabs.FINANCE_OFFERS;
        }
        switch (deskingTab) {
          case Tabs.SELECTED:
            return true;
          case Tabs.FINANCE_OFFERS: {
            return shouldShowFinanceTab({ financeApplications, deal, gb });
          }
          default:
            return shouldDisplayFinanceType(car, deal, dealer, deskingTab);
        }
      }),
    [financeApplications, car, deal, dealer],
  );

  const fetchFinanceApplications = useCallback(async () => {
    setLoading(true);
    const applications = (await FinanceApplicationService.get(
      deal?.id,
      car?.id,
      [FinanceApplicationStatus.ACTIVE, FinanceApplicationStatus.FAILED],
    )) as FinanceApplication[];
    setFinanceApplications(applications);
    lendingOffersProps.setFinanceApplicationCount(applications.length);
    setLoading(false);
  }, [deal?.id, car?.id]);

  const fetchFinanceResponses = useCallback(async () => {
    if (financeApplications?.length) {
      setLoading(true);
      const responses = (await FinanceResponseService.getForApplications(
        financeApplications.map(a => a.id),
      )) as FinanceResponse[];
      setFinanceResponses(responses);
      lendingOffersProps.setFinanceOfferCount(responses.length);
      setLoading(false);
    }
  }, [financeApplications]);

  useEffect(() => {
    if (!deal?.id || !car?.id) {
      return () => {};
    }
    // Listen for new application sent events
    const applicationSentEvent = {
      eventType: EventType.DEAL_FINANCE_APPLICATION_SENT,
      dealId: deal?.id,
    };
    WebsocketService.subscribe(applicationSentEvent, fetchFinanceApplications);

    // Listen for application reset events
    const applicationResetEvent = {
      eventType: EventType.DEAL_FINANCE_APPLICATION_RESET,
      dealId: deal?.id,
    };
    WebsocketService.subscribe(applicationResetEvent, fetchFinanceApplications);

    (async () => {
      await fetchFinanceApplications();
    })().catch(() => {});

    return () => {
      WebsocketService.unsubscribe(applicationSentEvent);
      WebsocketService.unsubscribe(applicationResetEvent);
    };
  }, [deal?.id, car?.id]);

  useEffect(() => {
    if (!financeApplications) {
      return;
    }
    if (financeApplications.length === 0 && !deal.submittedToLendersAt) {
      setTabPath(false);
      return;
    }
    // Listen for new response event
    const responseEvent = {
      eventType: EventType.DEAL_FINANCE_RESPONSE_RECEIVED,
      dealId: deal?.id,
    };
    WebsocketService.subscribe(responseEvent, fetchFinanceResponses);

    (async () => {
      await fetchFinanceResponses();
    })().catch(() => {});

    return () => {
      WebsocketService.unsubscribe(responseEvent);
    };
  }, [financeApplications, deal.submittedToLendersAt]);

  useEffect(() => {
    if (
      isFinanceOffersTab &&
      visibleTabs.includes(Tabs.FINANCE_OFFERS) &&
      activeTabIndex !== visibleTabs.length - 1
    ) {
      // set active index to the last tab (which is the Finance Offers tab)
      setActiveTabIndex(visibleTabs.length - 1);
    }
  }, [isFinanceOffersTab, visibleTabs]);

  // display lease if the pencil has any offers for it and car is not used
  const displayLease =
    car && car?.condition !== Condition.USED && hasLeaseOffers(pencil);

  const getDefaultActiveIndex = () => {
    let financeType = getSelectedFinanceType({ car, deal, dealer });
    // Double check that this pencil has lease offers
    // If not, default to CASH
    if (financeType === FinanceType.LEASE && !displayLease) {
      financeType = FinanceType.CASH;
    }
    const tabIndex = deskingTabs.findIndex(tab => tab === financeType);
    return Math.max(tabIndex, 0);
  };

  const onTabChange = useCallback(
    (_, data) => {
      const selectedTab = isNumber(data?.activeIndex)
        ? data.panes?.[data?.activeIndex]?.menuItem?.key
        : null;
      // the form submission code is located outside, use this variable to track when finance offers are being edited
      setTabPath(selectedTab === Tabs.FINANCE_OFFERS);
      if (selectedTab) {
        sendData(
          amplitudeEventsDeskingCTR.DESKING_TAB_CLICKED,
          safeParseStringEventObject({
            dealId: deal.id,
            deskingTab: selectedTab,
          }),
        );
        setActiveTabIndex(data?.activeIndex);
        if (lendingOffersProps.showRicTab) {
          lendingOffersProps.setShowRicTab(false);
        }
      }
    },
    [setTabPath, lendingOffersProps.showRicTab],
  );

  // Determine which tabs should display a warning icon
  const financeTypesWithErrors: DeskingTab[] = uniq(
    (errors ?? []).reduce((result, error) => {
      if (error.financeType) {
        result.push(error.financeType);
      }
      if (error.isSelected) {
        result.push(Tabs.SELECTED);
      }
      return result;
    }, [] as DeskingTab[]),
  );

  const getDeskingTabTitle = (deskingTab: DeskingTab) => {
    let title = deskingTabTitles[deskingTab];

    if (deskingTab === Tabs.SELECTED) {
      const selectedOfferCount = deskingOffers.filter(o => o.isSelectedOffer)
        .length;
      title = `${title} (${selectedOfferCount})`;
    } else if (deskingTab === Tabs.FINANCE_OFFERS) {
      let offerCount = financeResponses?.length ?? 0;
      if (offerCount > 0 && !dealer.flags.showCounterOffer) {
        offerCount =
          financeResponses?.filter(
            response => response.status !== FinanceResponseStatus.COUNTERED,
          ).length ?? 0;
      }
      title = `${title} (${offerCount})`;
    }

    return {
      key: deskingTab,
      icon: financeTypesWithErrors.includes(deskingTab) ? 'warning sign' : null,
      content: title,
    };
  };

  const renderTabContent = (
    deskingTab: DeskingTab,
    applications: FinanceApplication[] | null,
    responses: FinanceResponse[] | null,
  ) => {
    if (!pencil) {
      return null;
    }

    return deskingTab === Tabs.FINANCE_OFFERS ? (
      <LendingOffers
        {...lendingOffersProps}
        car={car}
        deal={deal}
        dealer={dealer}
        pencil={pencil}
        showLendingOffers
        fetchedApplications={applications}
        fetchedResponses={responses}
        forwardDealToFNI={props.forwardDealToFNI}
        fetchFinanceResponses={fetchFinanceResponses}
      />
    ) : (
      <DeskingOfferList
        {...props}
        car={car}
        deal={deal}
        dealer={dealer}
        desking={desking}
        deskingOffers={deskingOffers}
        deskingPayments={deskingPayments}
        displayAllSelected={deskingTab === Tabs.SELECTED}
        displayExpiredIncentiveNotice={props.displayExpiredIncentiveNotice}
        errors={errors}
        financeType={deskingTab === Tabs.SELECTED ? undefined : deskingTab}
        pencil={pencil}
        removeExpiredIncentivesRequest={removeExpiredIncentivesRequest}
        removeUnavailableAccessoriesRequest={
          removeUnavailableAccessoriesRequest
        }
        displayUnavailableAccessoriesNotice={
          props.displayUnavailableAccessoriesNotice
        }
        sendAmplitudeEvent={(event, action) => {
          const events = amplitudeEventsDesking[event][action];
          sendData(events, getCarInfo(car));
        }}
      />
    );
  };

  const panes = visibleTabs.map(deskingTab => ({
    deskingTab,
    menuItem: getDeskingTabTitle(deskingTab),
    render: () =>
      renderTabContent(deskingTab, financeApplications, financeResponses),
  }));

  if (!isNumber(activeTabIndex) || (activeTabIndex + 1 > panes?.length ?? 0)) {
    const defaultActiveIndex = getDefaultActiveIndex();
    /**
     * if defaultActiveIndex is more than panes length then it will create an infinite loop renders
     * to prevent this, we have to set active tab index as first tab.
     * This case will happen if the vehicle doesn't have any oemOffers or incentiveOffers
     * */
    if (defaultActiveIndex >= panes?.length) {
      setActiveTabIndex(0);
    } else {
      setActiveTabIndex(defaultActiveIndex);
    }
  }
  return (
    <div className="desking-offers-container">
      <LoadingOverlay active={loading || !car?.id}>
        <Tab
          className="offer-list-tabs"
          menu={{ secondary: true, pointing: true }}
          activeIndex={activeTabIndex ?? 0}
          panes={panes}
          onTabChange={onTabChange}
        />
      </LoadingOverlay>
      <RehashOffers
        dealId={deal.id}
        financeApplications={financeApplications}
        financeResponses={financeResponses}
      />
    </div>
  );
};

export default DeskingOffers;
